import { AnyAction } from 'redux';
import { AxiosError, AxiosPromise, AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'data/root.reducers';
import { ServerResponse } from 'data/types';
import { CollectionFunction } from 'data/collectionFunction/collectionFunction.types';
import { CollectionFunctionGroup } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { EnabledService, SupportedFeatures } from 'data/supportedFeatures/supportedFeatures.types';
import { Network } from 'data/network/network.types';

export const EWARRANT_USER_CATEGORY_SETTINGS = 'EWarrantSettings';

export const API_PATH_EWARRANT = '/e-warrants';
export const API_PATH_EWARRANT_DETAIL = `${API_PATH_EWARRANT}/detail`;
export const API_PATH_EWARRANT_GENERATE = `${API_PATH_EWARRANT}/generate`;
export const API_PATH_EWARRANT_INTERCEPT_STATUS = `${API_PATH_EWARRANT}/intercept-status`;
export const API_PATH_EWARRANT_AUTH_STATUS = `${API_PATH_EWARRANT}/auth-status`;
export const API_PATH_EWARRANT_TASK_STATUS = `${API_PATH_EWARRANT}/task-status`;
export const API_PATH_EWARRANT_DOCUMENT_DOWNLOAD = `${API_PATH_EWARRANT}/documents/download`;

export const GO_TO_EWARRANT_LIST = 'GO_TO_EWARRANT_LIST';
export const GO_TO_EWARRANT_VIEW = 'GO_TO_EWARRANT_VIEW';
export const GET_EWARRANT_LIST = 'GET_EWARRANT_LIST';
export const GET_EWARRANT_LIST_PENDING = 'GET_EWARRANT_LIST_PENDING';
export const GET_EWARRANT_LIST_FULFILLED = 'GET_EWARRANT_LIST_FULFILLED';
export const GET_EWARRANT_LIST_REJECTED = 'GET_EWARRANT_LIST_REJECTED';
export const GET_EWARRANT_DETAIL = 'GET_EWARRANT_DETAIL';
export const GET_EWARRANT_DETAIL_PENDING = 'GET_EWARRANT_DETAIL_PENDING';
export const GET_EWARRANT_DETAIL_FULFILLED = 'GET_EWARRANT_DETAIL_FULFILLED';
export const GET_EWARRANT_DETAIL_REJECTED = 'GET_EWARRANT_DETAIL_REJECTED';
export const SELECT_EWARRANT = 'SELECT_EWARRANT';
export const GENERATE_EWARRANT_FULFILLED = 'GENERATE_EWARRANT_FULFILLED';
export const GENERATE_WARRANT = 'GENERATE_WARRANT';
export const GENERATE_WARRANT_PENDING = 'GENERATE_WARRANT_PENDING';
export const GENERATE_WARRANT_FULFILLED = 'GENERATE_WARRANT_FULFILLED';
export const GENERATE_WARRANT_REJECTED = 'GENERATE_WARRANT_REJECTED';
export const UPDATE_EWARRANT_INTERCEPT_STATUS = 'UPDATE_EWARRANT_INTERCEPT_STATUS';
export const UPDATE_EWARRANT_INTERCEPT_STATUS_PENDING = 'UPDATE_EWARRANT_INTERCEPT_STATUS_PENDING';
export const UPDATE_EWARRANT_INTERCEPT_STATUS_FULFILLED = 'UPDATE_EWARRANT_INTERCEPT_STATUS_FULFILLED';
export const UPDATE_EWARRANT_INTERCEPT_STATUS_REJECTED = 'UPDATE_EWARRANT_INTERCEPT_STATUS_REJECTED';
export const UPDATE_EWARRANT_AUTH_STATUS = 'UPDATE_EWARRANT_AUTH_STATUS';
export const UPDATE_EWARRANT_AUTH_STATUS_PENDING = 'UPDATE_EWARRANT_AUTH_STATUS_PENDING';
export const UPDATE_EWARRANT_AUTH_STATUS_FULFILLED = 'UPDATE_EWARRANT_AUTH_STATUS_FULFILLED';
export const UPDATE_EWARRANT_AUTH_STATUS_REJECTED = 'UPDATE_EWARRANT_AUTH_STATUS_REJECTED';
export const UPDATE_EWARRANT_TASK_STATUS = 'UPDATE_EWARRANT_TASK_STATUS';
export const UPDATE_EWARRANT_TASK_STATUS_PENDING = 'UPDATE_EWARRANT_TASK_STATUS_PENDING';
export const UPDATE_EWARRANT_TASK_STATUS_FULFILLED = 'UPDATE_EWARRANT_TASK_STATUS_FULFILLED';
export const UPDATE_EWARRANT_TASK_STATUS_REJECTED = 'UPDATE_EWARRANT_TASK_STATUS_REJECTED';

export enum EWarrantPrivileges {
  View = 'ewarrant_view',
  Edit = 'ewarrant_write',
  Delete = 'ewarrant_delete'
}

export enum EWarrantStatus {
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected'
}

/**
 * The two statuses the operator's decision can move an e-warrant to. Xcipio takes these
 * verbatim as its AUTHSTATUS, so the decision endpoint is typed to them rather than to
 * EWarrantStatus — Pending is what an e-warrant arrives as, never something the portal
 * writes back.
 */
export type EWarrantDecisionStatus = EWarrantStatus.Approved | EWarrantStatus.Rejected;

/**
 * The two statuses an operator's decision can move one LI task to. Xcipio takes these
 * verbatim as the EWSLITASK TASKSTATUS, and the backend records the same value in the ETSI
 * payload's own task:Status.
 *
 * Deliberately not EWarrantStatus: a task's vocabulary is Xcipio's provisioning one
 * (Active, AwaitingProvisioning, ...) rather than the Approved/Rejected an authorisation
 * is decided in, so Approved has no meaning here - an approved task is an Active one.
 */
export enum EWarrantTaskStatus {
  Active = 'Active',
  Rejected = 'Rejected'
}

/** Values Xcipio accepts for the EWSAUTH INTTERCEPTSTATUS parameter. */
export enum EWarrantInterceptStatus {
  InProgress = 'Inprogress'
}

/**
 * The national provisioning-status dictionary Xcipio reports back in
 * INTTERCEPTSTATUS. Distinct from EWarrantInterceptStatus above, which is only what
 * the portal writes when it starts a generation.
 */
export enum EWarrantInterceptStatusValue {
  NotCreated = 'NotCreated',
  CreateUnderway = 'CreateUnderway',
  CreateSuccess = 'CreateSuccess',
  CreateError = 'CreateError',
  Created = 'Created'
}

export enum EWarrantMenuActions {
  View = 'view',
  Approve = 'approve',
  Generate = 'generate'
}

/**
 * Approval details carried by the inbound ETSI TS 103 120 HI1Message.
 */
export type EWarrantApproval = {
  type: string
  reference: string
  approverName: string
  approverRole: string
  /** From common:ApproverContactDetails — "" when the approver carries no contact for it. */
  approverEmail: string
  approverPhone: string
  timestamp: string
  isEmergency: boolean
};

/**
 * The Authorisation object of the inbound HI1Message. Everything that describes the
 * warrant itself lives here — the e-warrant record around it only carries the CSP's
 * decision state, its documents and its service specific information blocks.
 */
export type EWarrantAuthorisation = {
  /** ETSI HI-1 object identifier — the id of the e-warrant as a whole. */
  HI1OBJECTID: string
  /** Identifier of the HI-1 sender; keys the e-warrant row in Xcipio with HI1OBJECTID. */
  SENDERID: string
  name: string
  description: string
  sourceWarrantId: string
  sourceAgency: string
  receivingCsp: string
  receivedAt: string
  reference: string
  /** ETSI auth:AuthorisationPriority, read down to its DictionaryEntry value. */
  priority: string
  legalType: string
  typeOfCase: string
  desiredStatus: string
  startTime: string
  endTime: string
  creationTimestamp: string
  jurisdiction: string
  legalEntity: string
  cspId: string
  countryCode: string
  flags: string[]
  approval: EWarrantApproval
  manualInformation: string
};

export type EWarrantDocument = {
  docId: string
  documentName: string
  documentType: string
  contentType: string
  checksumType: string
  checksum: string
  sizeBytes: number
  isPrimary: boolean
  hasContent: boolean
  /** Xcipio DOCUMENTLOC - where the file is stored on the Xcipio side. */
  documentLocation: string
  /**
   * The decision recorded against this document in Xcipio's DOCSTATUS, read the same way
   * the e-warrant's own status is read out of AUTHSTATUS. Pending means the document is
   * still undecided.
   */
  docStatus: EWarrantStatus
};

export type EWarrantTargetIdentifier = {
  format: string
  value: string
};

/**
 * One service specific information block of the HI1Message. A single e-warrant can
 * carry several of them — one per target tasked under the same authorisation.
 */
export type EWarrantServices = {
  reference: string
  /**
   * Where the task actually is, from task:Status in the HI1 object — the field Xcipio
   * maintains. Shown as Actual Status.
   */
  actualStatus: string
  /**
   * Where the LEA asked this task to be, from task:DesiredStatus in the HI1 object. Nothing
   * on this side ever writes it. Shown as Current Status.
   */
  currentStatus: string
  /**
   * The decision recorded against this target, from Xcipio's own EWSLITASK.TASKSTATUS
   * column — the field the decision banner reads.
   *
   * Distinct from actualStatus and not always equal to it: approving an update moves the
   * column alone, leaving the ETSI task:Status behind. Empty means undecided; nothing
   * stands in for it.
   */
  taskStatus: string
  deliveryType: string
  handoverFormat: string
  /** Where the IRI goes. Empty for a CC-only task, which has no IRI destination. */
  deliveryEndpoint: string
  /**
   * Where the CC goes. Empty unless the task delivers CC; the same address as
   * deliveryEndpoint when it hands both streams to one collector.
   */
  ccDeliveryEndpoint: string
  cspId: string
  provisioningTime: string
  deprovisioningTime: string
  startTime: string
  endTime: string
  serviceTypes: string[]
  targetIdentifiers: EWarrantTargetIdentifier[]
};

/**
 * An e-warrant as stored after parsing an inbound HI1Message. The portal is a pure
 * receiver: it never authors e-warrants, it only tracks a Pending -> Approved/Rejected
 * decision on what the LEA dispatched.
 */
export type EWarrant = {
  id: string
  status: EWarrantStatus
  /**
   * The Xcipio AUTHSTATUS this e-warrant's status was mapped from, untranslated. Needed
   * because the mapping is many to one — AwaitingApproval and AwaitingUpdateApproval both
   * read as Pending, but only the latter's Approve has to sync an already-provisioned
   * warrant's dates instead of offering Generate Warrant. See adaptAuthStatus.
   */
  rawStatus: string
  /** Xcipio INTTERCEPTSTATUS — how far the CSP side provisioning has got. */
  interceptStatus: string
  decidedAt: string | null
  decidedBy: string | null
  decisionNote: string
  authorisation: EWarrantAuthorisation
  documents: EWarrantDocument[]
  services: EWarrantServices[]
};

/**
 * One row of `GET /e-warrants`. The backend flattens an EWSAUTH row in Xcipio down to
 * these fields — its identifiers plus the columns the list table shows. It
 * deliberately carries less than an `EWarrant`: the documents, services and targets of
 * an e-warrant are not part of the list read.
 */
export type EWarrantListItem = {
  /** Xcipio's own row identifier. */
  id: string
  /** ETSI HI-1 object identifier; keys the EWSAUTH row together with senderId. */
  hi1ObjectId: string
  senderId: string
  /** Xcipio AUTHREFERENCE — the warrant reference the LEA authorised under. */
  warrant: string
  /** Resolved from the AuthorisationLegalEntity of the raw HI-1 authorisation object. */
  requestingAgency: string | null
  /** Xcipio STARTDATETIME. */
  received: string
  /** Xcipio AUTHSTATUS, e.g. "AwaitingApproval". */
  status: string
  /** Xcipio INTTERCEPTSTATUS, e.g. "NotCreated". */
  interceptStatus: string
};

/**
 * A document as the detail read carries it: `docStatus` is the raw Xcipio DOCSTATUS
 * ("AwaitingApproval", ...), exactly as `status` on the read below is the raw AUTHSTATUS.
 */
export type EWarrantRawDocument = Omit<EWarrantDocument, 'docStatus'> & {
  docStatus: string
};

/**
 * Body of `GET /e-warrants/detail` — the backend assembles it from the three Xcipio
 * tables an e-warrant is spread over. `status` arrives as the raw Xcipio AUTHSTATUS, and
 * `interceptStatus` is not part of the detail read (the list already carries it), so the
 * detail is merged onto the entity the list produced rather than replacing it. There is no
 * separate `rawStatus` on the wire — the adapter derives it from `status` itself, the same
 * way it maps `status` to the `EWarrantStatus` the store holds.
 */
export type EWarrantDetailResponse = Omit<EWarrant, 'status' | 'rawStatus' | 'interceptStatus' | 'documents'> & {
  status: string
  documents: EWarrantRawDocument[]
};

/** What a detail read contributes to an `EWarrant` already in the store. */
export type EWarrantDetailPatch = EWarrant;

export type EWarrantGridRowData = {
  id: string
  /**
   * Not displayed — carried so the list's own actions can address a row. SENDERID and
   * HI1OBJECTID together key the EWSAUTH row, and `id` is only the latter.
   */
  senderId: string
  name: string
  sourceAgency: string
  receivingCsp: string
  target: string
  serviceTypes: string
  documentCount: number
  receivedAt: string
  status: EWarrantStatus
  interceptStatus: string
};

/**
 * Payload of the intercept status update — only the id of the e-warrant to move and
 * the status to move it to. Xcipio needs the whole EWSAUTH row back on an update, but
 * the backend reads that row itself, so it never travels through here.
 *
 * HI1OBJECTID is the name Xcipio keys off, so it is not camelCased.
 */
export type EWarrantInterceptStatusPayload = {
  HI1OBJECTID: string
  SENDERID: string
  InterceptStatus: EWarrantInterceptStatus
};

/**
 * One document's decision, as the authorisation status update carries it. DocId is the
 * EWSDOCUMENT row's own HI1OBJECTID, which keys it together with the authorisation's
 * SENDERID — the sender is not repeated per document because every document of an
 * e-warrant shares it.
 *
 * Not named HI1OBJECTID: the body already carries the authorisation's HI1OBJECTID at its
 * top level, and two keys of the same name addressing different rows would invite writing
 * the wrong one.
 */
export type EWarrantDocumentStatusPayload = {
  DocId: string
  DocStatus: EWarrantDecisionStatus
};

/**
 * Payload of the authorisation status update — the id of the e-warrant to decide, the
 * status the operator decided on, and the decision note that explains it. The backend
 * writes the note to ERRMSG on the same EWSAUTH row, which is where the reason a status
 * was set is kept.
 *
 * `Documents` carries only the documents the operator explicitly decided. The backend
 * writes each one's DOCSTATUS before it moves AUTHSTATUS, and writes none at all when the
 * list is empty.
 *
 * Named as Xcipio keys off, so HI1OBJECTID is not camelCased.
 */
export type EWarrantAuthStatusPayload = {
  HI1OBJECTID: string
  SENDERID: string
  AuthStatus: EWarrantDecisionStatus
  MessageText: string
  Documents: EWarrantDocumentStatusPayload[]
  /**
   * Only sent when approving an e-warrant whose raw AUTHSTATUS is AwaitingUpdateApproval —
   * its presence is what tells the backend to find the warrant already provisioned for
   * this e-warrant (by its comments tag) and revise its dates, instead of leaving it
   * untouched. Named as Xcipio's EWSAUTH columns are, like HI1OBJECTID/SENDERID above.
   */
  STARTDATETIME?: string
  ENDDATETIME?: string
};

/**
 * Payload of the LI task status update — the decision an operator made against one target.
 *
 * HI1OBJECTID and SENDERID key the authorisation, and LIID picks out the one EWSLITASK row
 * among the tasks hanging off it: the backend reads that table by the authorisation, so the
 * LIID is what narrows the read to a single target. It is the `reference` the detail read
 * carries on each service.
 *
 * Named as Xcipio keys off, like EWarrantAuthStatusPayload above.
 */
export type EWarrantTaskStatusPayload = {
  HI1OBJECTID: string
  SENDERID: string
  LIID: string
  TaskStatus: EWarrantTaskStatus
};

/**
 * A form template, identified rather than carried: the backend resolves a template by
 * category and name and only ever puts its id on what it builds, so the template body
 * — several kilobytes of field definitions per template — is left out of the payload.
 */
export type EWarrantGenerateTemplate = {
  id: string
  name: string
  category: string
  /**
   * Every field of the template at its initial value, as getCaseOptionTemplateValue
   * resolves it — what the warrant form would have put on the case it creates. Only
   * meaningful for CaseOptions templates; the backend copies it onto the case and
   * overrides only the flags it derives from the e-warrant itself.
   */
  options?: Record<string, unknown>
};

/**
 * Body of the generate warrant request: the e-warrant, the collection functions built
 * from the templates its handover formats map onto, and the LEA whose SENDERID the
 * e-warrant was received under.
 */
export type EWarrantGenerateRequest = {
  eWarrant: EWarrant
  collectionFunctions: CollectionFunction[]
  lea: CollectionFunctionGroup
  /**
   * The ontology, forwarded as-is. The backend resolves each target's Access
   * Functions against it, so generation fails without it.
   */
  supportedFeatures?: SupportedFeatures
  /**
   * What this delivery function actually has enabled, as WarrantConfiguration
   * computes it from the ontology's feature nodes. Sent so the backend narrows to the
   * same Access Functions and services the UI would offer, rather than re-deriving it.
   */
  enabledAfTypeList?: string[]
  /**
   * The enabled services as full ontology entries. The backend maps each e-warrant
   * service's serviceTypes onto ontology services by matching them against every
   * entry's ewsServiceTypes, so this is what makes a case's services resolvable —
   * generation fails without it.
   */
  enabledServices?: EnabledService[]
  /**
   * The templates the backend resolves case options against. Every generated case
   * points at the CaseOptions template matching whether its service asks for location,
   * so the "With Location" and "Without Location" pair travels with the request —
   * template ids differ per deployment, which is why the backend matches on name.
   */
  templates?: EWarrantGenerateTemplate[]
  /**
   * The XCP networks, as the collection function form's ownnetid dropdown lists them.
   * The backend puts the default any-address network matching each created Collection
   * Function's destination IP version on its HI2 interface.
   */
  networks?: Network[]
};

/** Outcome the backend reports for a warrant generation request. */
export type EWarrantGeneration = {
  eWarrantId: string
  generationId: string
  status: string
  generatedAt: string
  message: string
  /** The actual name Xcipio stored the generated warrant under; unset on a failed generation. */
  warrantName?: string
};

export interface EWarrantUIState {
  isLoading: boolean
}

export interface UpdateEWarrantInterceptStatusAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_INTERCEPT_STATUS
  payload: AxiosPromise<ServerResponse<void>>
}

export interface UpdateEWarrantInterceptStatusPendingAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_INTERCEPT_STATUS_PENDING
  payload: null
}

export interface UpdateEWarrantInterceptStatusFulfilledAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_INTERCEPT_STATUS_FULFILLED
  payload: AxiosResponse<ServerResponse<void>>
}

export interface UpdateEWarrantInterceptStatusRejectedAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_INTERCEPT_STATUS_REJECTED
  payload: AxiosError<ServerResponse<void>>
}

export interface UpdateEWarrantAuthStatusAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_AUTH_STATUS
  payload: AxiosPromise<ServerResponse<void>>
}

export interface UpdateEWarrantAuthStatusPendingAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_AUTH_STATUS_PENDING
  payload: null
}

export interface UpdateEWarrantAuthStatusFulfilledAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_AUTH_STATUS_FULFILLED
  payload: AxiosResponse<ServerResponse<void>>
}

export interface UpdateEWarrantAuthStatusRejectedAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_AUTH_STATUS_REJECTED
  payload: AxiosError<ServerResponse<void>>
}

export interface UpdateEWarrantTaskStatusAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_TASK_STATUS
  payload: AxiosPromise<ServerResponse<void>>
}

export interface UpdateEWarrantTaskStatusPendingAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_TASK_STATUS_PENDING
  payload: null
}

export interface UpdateEWarrantTaskStatusFulfilledAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_TASK_STATUS_FULFILLED
  payload: AxiosResponse<ServerResponse<void>>
}

export interface UpdateEWarrantTaskStatusRejectedAction extends AnyAction {
  type: typeof UPDATE_EWARRANT_TASK_STATUS_REJECTED
  payload: AxiosError<ServerResponse<void>>
}

export interface GenerateWarrantAction extends AnyAction {
  type: typeof GENERATE_WARRANT
  payload: AxiosPromise<ServerResponse<EWarrantGeneration>>
}

export interface GenerateWarrantPendingAction extends AnyAction {
  type: typeof GENERATE_WARRANT_PENDING
  payload: null
}

export interface GenerateWarrantFulfilledAction extends AnyAction {
  type: typeof GENERATE_WARRANT_FULFILLED
  payload: AxiosResponse<ServerResponse<EWarrantGeneration>>
}

export interface GenerateWarrantRejectedAction extends AnyAction {
  type: typeof GENERATE_WARRANT_REJECTED
  payload: AxiosError<ServerResponse<EWarrantGeneration>>
}

export interface EWarrantState {
  entities: EWarrant[]
  selected: EWarrantGridRowData[]
  ui: EWarrantUIState
}

export interface EWarrantThunkAction
  extends ThunkAction<Promise<void>, RootState, void, AnyAction> { }

export interface GoToEWarrantListAction extends AnyAction {
  type: typeof GO_TO_EWARRANT_LIST
}

export interface GoToEWarrantViewAction extends AnyAction {
  type: typeof GO_TO_EWARRANT_VIEW
}

/**
 * The list read travels unwrapped: the grid's server side datasource reads the rows and
 * the row count straight off the axios response, so the reducer is what widens them into
 * the EWarrant the store holds.
 */
export interface GetEWarrantListAction extends AnyAction {
  type: typeof GET_EWARRANT_LIST
  payload: Promise<AxiosResponse<ServerResponse<EWarrantListItem>>>
}

export interface GetEWarrantListPendingAction extends AnyAction {
  type: typeof GET_EWARRANT_LIST_PENDING
  payload: null
}

export interface GetEWarrantListFulfilledAction extends AnyAction {
  type: typeof GET_EWARRANT_LIST_FULFILLED
  payload: AxiosResponse<ServerResponse<EWarrantListItem>>
}

export interface GetEWarrantListRejectedAction extends AnyAction {
  type: typeof GET_EWARRANT_LIST_REJECTED
  payload: Error
}

export interface GetEWarrantDetailAction extends AnyAction {
  type: typeof GET_EWARRANT_DETAIL
  payload: Promise<EWarrantDetailPatch>
}

export interface GetEWarrantDetailPendingAction extends AnyAction {
  type: typeof GET_EWARRANT_DETAIL_PENDING
  payload: null
}

export interface GetEWarrantDetailFulfilledAction extends AnyAction {
  type: typeof GET_EWARRANT_DETAIL_FULFILLED
  payload: EWarrantDetailPatch
}

export interface GetEWarrantDetailRejectedAction extends AnyAction {
  type: typeof GET_EWARRANT_DETAIL_REJECTED
  payload: Error
}

export interface SelectEWarrantAction extends AnyAction {
  type: typeof SELECT_EWARRANT
  payload: EWarrantGridRowData[]
}

export interface GenerateEWarrantFulfilledAction extends AnyAction {
  type: typeof GENERATE_EWARRANT_FULFILLED
  payload: { id: string, document: EWarrantDocument }
}

export type EWarrantActionTypes =
  | GoToEWarrantListAction
  | GoToEWarrantViewAction
  | GetEWarrantListAction
  | GetEWarrantListPendingAction
  | GetEWarrantListFulfilledAction
  | GetEWarrantListRejectedAction
  | GetEWarrantDetailAction
  | GetEWarrantDetailPendingAction
  | GetEWarrantDetailFulfilledAction
  | GetEWarrantDetailRejectedAction
  | SelectEWarrantAction
  | GenerateEWarrantFulfilledAction
  | GenerateWarrantAction
  | GenerateWarrantPendingAction
  | GenerateWarrantFulfilledAction
  | GenerateWarrantRejectedAction
  | UpdateEWarrantInterceptStatusAction
  | UpdateEWarrantInterceptStatusPendingAction
  | UpdateEWarrantInterceptStatusFulfilledAction
  | UpdateEWarrantInterceptStatusRejectedAction
  | UpdateEWarrantAuthStatusAction
  | UpdateEWarrantAuthStatusPendingAction
  | UpdateEWarrantAuthStatusFulfilledAction
  | UpdateEWarrantAuthStatusRejectedAction
  | UpdateEWarrantTaskStatusAction
  | UpdateEWarrantTaskStatusPendingAction
  | UpdateEWarrantTaskStatusFulfilledAction
  | UpdateEWarrantTaskStatusRejectedAction;
